import React from 'react';
import { ListRenderItem, StyleSheet, View } from 'react-native';

import { AccentBadge } from '@components/Badge';
import FlatList, { EncapsulatedFlatListProps } from '@components/FlatList';
import { DeleteSwipeable } from '@components/Swipeable';
import { LargeUserCard, SmallUserCard, UserCardProps, UserData } from '@components/User/Card';
import { ConditionalWrapper } from '@helpers/Rendering';

type LargeUserCardListProps<T extends UserData> = EncapsulatedFlatListProps<
  T,
  Omit<UserCardProps<T>, 'data'>
> & {
  /** Whether this user can expose the swipe-to-delete action. */
  deletable?: (user: T) => boolean;
  /** Whether this user's delete action is currently in flight. */
  deleting?: (user: T) => boolean;
  onDelete?: (user: T) => void;
  isAdmin?: (user: T) => boolean;
};

export function LargeUserCardList<T extends UserData>({
  itemProps,
  deletable,
  deleting,
  onDelete,
  isAdmin,
  ...props
}: LargeUserCardListProps<T>) {
  const renderItem: ListRenderItem<T> = ({ item }) => (
    <ConditionalWrapper
      condition={Boolean(deletable?.(item))}
      wrapper={(children) => (
        <DeleteSwipeable
          loading={deleting ? deleting(item) : false}
          onDelete={onDelete ? () => onDelete(item) : undefined}>
          {children}
        </DeleteSwipeable>
      )}>
      <View>
        {isAdmin && isAdmin(item) ? (
          <AccentBadge style={styles.adminBadge}>Admin</AccentBadge>
        ) : null}
        <LargeUserCard data={item} {...itemProps} />
      </View>
    </ConditionalWrapper>
  );

  const keyExtractor = (item: T, index: number) => item.userId;

  return (
    <FlatList
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      initialNumToRender={10}
      maxToRenderPerBatch={10}
      windowSize={5}
      {...props}
    />
  );
}

type SmallUserCardListProps<T extends UserData> = EncapsulatedFlatListProps<
  T,
  Omit<UserCardProps<T>, 'data'>
>;

export function SmallUserCardList<T extends UserData>({
  itemProps,
  ...props
}: SmallUserCardListProps<T>) {
  const renderItem: ListRenderItem<T> = ({ item }) => <SmallUserCard data={item} {...itemProps} />;

  const keyExtractor = (item: T, index: number) => item.userId;

  return (
    <FlatList
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      initialNumToRender={3}
      maxToRenderPerBatch={3}
      windowSize={5}
      {...props}
    />
  );
}

const styles = StyleSheet.create({
  adminBadge: {
    left: 5,
    position: 'absolute',
    top: 5,
    zIndex: 1,
  },
});
